import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { ClientList, ClientSettingList, Client } from './client.types';

// Standardized GET function for clients list
export async function fetchClients({
  clientCode = "admin",
  firstResult = -1,
  maxResult = 0,
  code,
  name,
  searched = true,
  ...rest
}: {
  clientCode?: string,
  firstResult?: number,
  maxResult?: number,
  code?: string,
  name?: string,
  searched?: boolean,
  [key: string]: any
}): Promise<ClientList> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  // Only add defined and non-empty parameters
  if (code !== undefined && code !== null && code !== '') params.code = code;
  if (name !== undefined && name !== null && name !== '') params.name = name;
  
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.LIST);
  return api.get<ClientList>(endpoint, params);
}

// GET all clients (no pagination)
// Calls: /client/?firstResult=-1&maxResult=0
export async function fetchAllClients(clientCode: string): Promise<ClientList> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.LIST);
  return api.get<ClientList>(endpoint, { firstResult: -1, maxResult: 0 });
}

// GET all clients for dropdown (explicit function for your requirement)
// Calls: /client/?firstResult=-1&maxResult=0
export async function fetchClientsForDropdown({
  clientCode,
}: {
  clientCode: string;
}): Promise<ClientList> {
  const params: Record<string, any> = {
    firstResult: -1,
    maxResult: 0,
  };

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.LIST);
  return api.get<ClientList>(endpoint, params);
}

// GET clients with pagination
export async function fetchClientsPaginated(
  clientCode: string, 
  firstResult: number, 
  maxResult: number, 
  searchTerm?: string
): Promise<ClientList> {
  const params: Record<string, string> = {
    firstResult: String(firstResult),
    maxResult: String(maxResult),
  };
  if (searchTerm) params.name = searchTerm;
  
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.LIST);
  return api.get<ClientList>(endpoint, params);
}

// GET clients count
export async function fetchClientsCount(
  clientCode: string, 
  {
    code,
    name,
    searched = true,
    ...rest
  }: {
    code?: string;
    name?: string;
    searched?: boolean;
    [key: string]: any;
  }
): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };
  
  // Only add defined and non-empty parameters
  if (code !== undefined && code !== null && code !== '') params.code = code;
  if (name !== undefined && name !== null && name !== '') params.name = name;
  
  // Add any other rest parameters
  Object.assign(params, rest);
  
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.COUNT);
  const response = await api.get<any>(endpoint, params);
  
  // Handle different response formats
  if (typeof response === 'number') {
    // API returns count directly as number
    return response;
  } else if (response && typeof response.count === 'number') {
    // API returns object with count property
    return response.count;
  }
  
  logger.warn('Invalid count response:', response);
  return 0;
}

// CREATE client
export async function createClient(
  clientCode: string, 
  { code, name }: { code: string; name: string }
): Promise<Client> {
  const payload = { code, name };
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.CREATE);
  return api.post<Client>(endpoint, payload);
}

// GET client settings
export async function fetchClientSettings(
  clientCode: string, 
  clientId: string
): Promise<ClientSettingList> {
  const params = {
    firstResult: "-1",
    maxResult: "0",
    clientId,
  };
  
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.SETTINGS);
  return api.get<ClientSettingList>(endpoint, params);
}

// UPDATE client setting
export async function updateClientSetting(
  clientCode: string, 
  { value, key, clientId, id }: { 
    value: string; 
    key: string; 
    clientId: string | number; 
    id: string | number; 
  }
) {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.UPDATE_SETTING);
  return api.put(endpoint, { value, key, clientId, id });
}

// CREATE client setting - POST /projectag/admin/clientSettings/
export async function addClientSetting(
  clientCode: string, 
  { value, key, clientId }: { 
    value: string; 
    key: string; 
    clientId: string | number; 
  }
) {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.SETTINGS);
  return api.post(endpoint, { value, key, clientId });
}